import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig, categories } from '@/mocks/categories';
import { getArticleBySlug, getAllArticleSlugs } from '@/mocks/articles';
import type { ArticleData, ArticleSection, FAQItem } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || siteConfig.domain;

function buildArticleJsonLd(article: ArticleData): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: siteUrl + '/article/' + article.slug,
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    inLanguage: 'en',
    author: {
      '@type': 'Organization',
      name: 'SuperBuySheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'SuperBuySheet',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/article/' + article.slug,
    },
    ...(article.faqs.length > 0
      ? {
          hasPart: article.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a.replace(/<[^>]*>/g, ''),
            },
          })),
        }
      : {}),
  };
}

function ArticleHeader({ article }: { article: ArticleData }) {
  return (
    <section className="bg-background-100 border-b border-background-200/70">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs font-medium text-foreground-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <span className="text-foreground-300">/</span>
          <span className="text-xs font-medium text-primary-600 whitespace-nowrap">{article.badge}</span>
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
          <i className="ri-article-line"></i>
          Updated July 2026
        </div>

        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
          {article.title}
        </h1>

        <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
          {article.description}
        </p>
      </div>
    </section>
  );
}

function SectionText({ section }: { section: ArticleSection }) {
  return (
    <div
      className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4"
      dangerouslySetInnerHTML={{ __html: section.paragraphs?.map(p => `<p>${p}</p>`).join('') || '' }}
    />
  );
}

function SectionCards({ section }: { section: ArticleSection }) {
  return (
    <div>
      {section.paragraphs && section.paragraphs.length > 0 && (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">{section.paragraphs[0]}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {section.cards?.map((card, i) => (
          <div key={i} className="p-4 bg-background-100 border border-background-200/70 rounded-xl">
            <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1.5">{card.title}</h4>
            <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionSteps({ section }: { section: ArticleSection }) {
  return (
    <div>
      {section.paragraphs && section.paragraphs.length > 0 && (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5" dangerouslySetInnerHTML={{ __html: section.paragraphs[0] }} />
      )}
      <div className="space-y-4">
        {section.steps?.map((step, i) => (
          <div key={i} className="flex gap-4 p-4 bg-background-100 border border-background-200/70 rounded-xl">
            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 font-bold text-sm">
              {i + 1}
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1">{step.title}</h4>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: step.desc }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionMistakes({ section }: { section: ArticleSection }) {
  return (
    <div>
      {section.paragraphs && section.paragraphs.length > 0 && (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">{section.paragraphs[0]}</p>
      )}
      <div className="space-y-3">
        {section.mistakes?.map((item, i) => (
          <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-xl">
            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
              <i className="ri-error-warning-line text-sm"></i>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1">{item.title}</h4>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionCompareTable({ section }: { section: ArticleSection }) {
  return (
    <div>
      {section.paragraphs && section.paragraphs.length > 0 && (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">{section.paragraphs[0]}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-background-100 border border-background-200/70 rounded-xl overflow-hidden text-sm">
          <thead>
            <tr className="bg-background-200/70">
              {section.compareHeaders?.map((h, i) => (
                <th key={i} className={`text-left px-4 py-3 font-semibold ${i === (section.compareHeaders!.length - 1) ? 'text-primary-700' : 'text-foreground-700'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-foreground-600">
            {section.compareRows?.map((row, ri) => (
              <tr key={ri} className="border-t border-background-200/70">
                {row.map((cell, ci) => (
                  <td key={ci} className={`px-4 py-3 ${ci === (row.length - 1) ? 'text-primary-700 font-medium' : ci === 0 ? 'font-medium text-foreground-950' : ''}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionCategoryGrid() {
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 my-5">
        {categories.map((cat) => (
          <a
            key={cat.seq}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 p-3 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:border-primary-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
              <i className={`${cat.icon} text-xs`}></i>
            </div>
            {cat.name}
          </a>
        ))}
      </div>
    </div>
  );
}

function ArticleFAQ({ faqs }: { faqs: FAQItem[] }) {
  return (
    <section id="faq">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {faqs.map((faq, i) => (
          <details key={i} className="group border border-background-200/70 rounded-xl overflow-hidden">
            <summary className="flex items-center justify-between px-5 py-4 cursor-pointer bg-background-100 hover:bg-background-200/50 transition-colors duration-150">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 pr-4">{faq.q}</h3>
              <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
            </summary>
            <div className="px-5 py-4 border-t border-background-200/70">
              <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }} />
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function RelatedArticles({ slugs }: { slugs: string[] }) {
  const relatedArticles = slugs
    .map((s) => getArticleBySlug(s))
    .filter((a): a is ArticleData => a !== undefined);

  if (relatedArticles.length === 0) return null;

  return (
    <div className="mt-10 p-5 bg-background-100 border border-background-200/70 rounded-xl">
      <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-3">Explore More Guides</h4>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/"
          className="px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-50 border border-background-200/70 rounded-full hover:text-primary-600 hover:border-primary-200 transition-all duration-200 whitespace-nowrap"
        >
          Home
        </Link>
        {relatedArticles.map((ra) => (
          <Link
            key={ra.slug}
            to={`/article/${ra.slug}`}
            className="px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-50 border border-background-200/70 rounded-full hover:text-primary-600 hover:border-primary-200 transition-all duration-200 whitespace-nowrap"
          >
            {ra.badge}
          </Link>
        ))}
      </div>
    </div>
  );
}

function ArticleCTA({ article }: { article: ArticleData }) {
  return (
    <div className="mt-12 p-6 md:p-8 bg-primary-500 text-background-50 rounded-2xl text-center">
      <h3 className="font-heading text-xl md:text-2xl font-bold mb-3">
        Ready to Start Shopping?
      </h3>
      <p className="text-sm md:text-base text-primary-50 mb-6 max-w-md mx-auto">
        Explore the complete Superbuy Spreadsheet collection on KASpreadsheet and discover thousands of curated products today.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href={siteConfig.mainSiteUrl}
          target="_blank"
          rel="noopener nofollow"
          className="w-full sm:w-auto px-7 py-3 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-white transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          Visit KASpreadsheet
        </a>
        <Link
          to="/"
          className="w-full sm:w-auto px-7 py-3 bg-primary-600 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-700 transition-all duration-200 cursor-pointer whitespace-nowrap border border-primary-400"
        >
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}

function renderSection(section: ArticleSection) {
  switch (section.type) {
    case 'text':
      return <SectionText section={section} />;
    case 'cards':
      return <SectionCards section={section} />;
    case 'steps':
      return <SectionSteps section={section} />;
    case 'mistakes':
      return <SectionMistakes section={section} />;
    case 'compare-table':
      return <SectionCompareTable section={section} />;
    case 'category-grid':
      return <SectionCategoryGrid />;
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article)) }}
        />

        <ArticleHeader article={article} />

        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="space-y-10 text-foreground-700 leading-relaxed">

              {/* TOC */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h4 className="font-heading text-base font-semibold text-foreground-950 mb-3">Table of Contents</h4>
                <ul className="space-y-1.5 text-sm text-foreground-600">
                  {article.sections.map((sec, i) => (
                    <li key={sec.id}>
                      <a href={`#${sec.id}`} className="hover:text-primary-600 transition-colors duration-200">
                        {i + 1}. {sec.title}
                      </a>
                    </li>
                  ))}
                  {article.faqs.length > 0 && (
                    <li>
                      <a href="#faq" className="hover:text-primary-600 transition-colors duration-200">
                        {article.sections.length + 1}. Frequently Asked Questions
                      </a>
                    </li>
                  )}
                </ul>
              </div>

              {/* Sections */}
              {article.sections.map((section) => (
                <section key={section.id} id={section.id}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  {renderSection(section)}
                </section>
              ))}

              {/* FAQ */}
              {article.faqs.length > 0 && <ArticleFAQ faqs={article.faqs} />}
            </div>

            <ArticleCTA article={article} />
            <RelatedArticles slugs={article.relatedSlugs} />
          </div>
        </article>

        {/* Disclaimer */}
        <section className="py-8 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="bg-secondary-50/50 border border-secondary-200/50 rounded-xl p-5 text-center">
              <p className="text-xs text-foreground-500">
                <strong className="text-foreground-700">Disclaimer:</strong> {siteConfig.disclaimer}
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}